import { useState } from "react";
import { durationLabel, formatTime } from "../../lib/date";
import { anchorOf, type Anchor } from "../../state/ui";
import { Menu } from "./Menu";

/**
 * Time dropdown: 15-minute steps; for an end time it lists the times after
 * the start with the resulting duration ("11:00 (1 h)"). Typing "10:40" also works.
 */
export function TimeSelect({ value, onChange, startMin, ariaLabel }: { value: number; onChange: (min: number) => void; startMin?: number; ariaLabel: string }) {
  const [menu, setMenu] = useState<Anchor | null>(null);
  const [text, setText] = useState<string | null>(null);

  const options: number[] = [];
  if (startMin === undefined) for (let m = 0; m < 1440; m += 15) options.push(m);
  else for (let m = startMin + 15; m <= startMin + 1440 - 15; m += 15) options.push(m);

  const commit = () => {
    if (text === null) return;
    const match = text.trim().match(/^(\d{1,2})(?:[:h.](\d{2}))?$/);
    setText(null);
    if (!match) return;
    const minutes = Number(match[1]) * 60 + Number(match[2] ?? 0);
    if (minutes >= 0 && minutes < 1440) onChange(startMin !== undefined && minutes <= startMin % 1440 ? minutes + 1440 : minutes);
  };

  return (
    <>
      <input
        className="time-input"
        aria-label={ariaLabel}
        value={text ?? formatTime(value)}
        onFocus={(e) => {
          setMenu(anchorOf(e.currentTarget));
          e.currentTarget.select();
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit();
            setMenu(null);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      {menu && (
        <Menu
          anchor={menu}
          minWidth={startMin !== undefined ? 150 : 110}
          onClose={() => setMenu(null)}
          items={options.map((m) => ({
            key: String(m),
            label: startMin !== undefined ? `${formatTime(m)} (${durationLabel(m - startMin)})` : formatTime(m),
            active: m === value || (startMin !== undefined && m % 1440 === value % 1440 && m > startMin),
            onSelect: () => {
              setText(null);
              onChange(m);
            }
          }))}
        />
      )}
    </>
  );
}
